<template>
  <v-row no-gutters>
    <v-col cols="12" class="mb-6">
      <div class="col-12 flex justify-start pa-0">
        <v-btn v-for="button in crmProfileRedirectButtons" class="mr-4 mb-2 white--text" color="blue" :key="button.path" @click="redirectToCRM(button.path)">
          <v-icon left>mdi-open-in-new</v-icon>
          {{ button.label }}
        </v-btn>
      </div>
      <v-card class="mt-4" :loading="loading">
        <v-card-title class="third white--text font-weight-bold">
          <span class="ml-2">Informations du Cavalier</span>
          <v-spacer />
          <v-icon v-if="!editable" size="28" color="white" class="justify-end" @click="editable = true">mdi-pencil</v-icon>
          <v-icon v-else size="28" color="white" class="justify-end" @click="editable = false">mdi-eye</v-icon>
        </v-card-title>
        <v-row no-gutters>
          <v-card-text>
            <v-form ref="form" v-model="formValid">
              <v-row>
                <v-col v-for="(field, index) in formFields" :key="`customer_form_${index}`" :cols="field.col" class="px-2">
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-subtitle>{{ field.label }} {{ editable && isRequired (field) ? ' *' : '' }}</v-list-item-subtitle>
                      <v-list-item-title v-if="customer[field.field_name] && !editable || field.readOnly">{{ getLabel(field) }}</v-list-item-title>
                      <v-list-item-title v-else-if="!customer[field.field_name] && !editable">--</v-list-item-title>
                      <v-list-item-title v-else>
                        <Select
                          v-if="field.viewType === 'Select'"
                          v-model="editableCustomer[field.field_name]"
                          :items="fieldItems(field)"
                          :placeholder="field.label"
                          :rules="getFieldRules(field)"
                        />

                        <TextField
                          v-if="field.viewType === 'Text'"
                          v-model="editableCustomer[field.field_name]"
                          :placeholder="field.label"
                          :rules="getFieldRules(field)"
                        />

                        <Date
                          v-if="field.viewType === 'Date Picker'"
                          v-model="editableCustomer[field.field_name]"
                          :placeholder="field.label"
                          :rules="getFieldRules(field)"
                        />
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
        </v-row>
        <v-divider v-if="editable" />
        <v-card-actions v-if="editable">
          <v-spacer />
          <Button
            :disabled="!formValid"
            label="Enregistrer"
            :action="saveCustomer"
            icon="mdi-content-save"
          />
        </v-card-actions>
      </v-card>
      <OrdersList :customer="customer" />
      <v-card class="mt-4" :loading="loadingNotes">
        <v-card-title class="third white--text font-weight-bold">
          <span class="ml-2">Annotations sur le cavalier</span>
          <v-spacer />
          <v-icon v-if="!editableNotes" size="28" color="white" class="justify-end" @click="editableNotes = true">mdi-pencil</v-icon>
          <v-icon v-else size="28" color="white" class="justify-end" @click="editableNotes = false">mdi-eye</v-icon>
        </v-card-title>
        <v-row no-gutters>
          <v-card-text>
            <v-textarea
              solo
              background-color="grey lighten-2"
              :readonly="!editableNotes"
              no-resize
              name="customerNotes"
              hide-details
              v-model="editableCustomer.pegaseNotes"
              :class="[{'default-cursor': !editableNotes} ]"
            ></v-textarea>
          </v-card-text>
        </v-row>
        <v-divider v-if="editableNotes" />
        <v-card-actions v-if="editableNotes">
          <v-spacer />
          <Button
            :disabled="changedNotes"
            label="Enregistrer"
            :action="saveCustomerNotes"
            icon="mdi-content-save"
          />
        </v-card-actions>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { ref, computed, watch, onMounted } from '@vue/composition-api';
import { mandatoryRule, formatDate, sendNotification, redirectToCRM } from '../../../helpers/utils';
import { updateCustomer } from '../../../helpers/request';
import Button from '../../common/Button';
import countries from '../../../configs/countries';
import store from '../../../store';

import OrdersList from '@/components/order/OrdersList.vue';

import Select from './Fields/Select';
import TextField from './Fields/TextField.vue';
import Date from './Fields/Date.vue';

const formFields = require('../../../configs/customer.json');

export default {
  components: { Button, Select, TextField, Date, OrdersList },
  props: {
    customer: {
      type: Object,
      default: null
    },
    updateContentType: {
      type: Function,
      default: null
    }
  },
  setup (props, context) {
    /* Customer Form */
    const editable = ref(false);
    const formValid = ref(false);
    const loading = ref(false);

    /* Notes */
    const editableNotes = ref(false);
    const loadingNotes = ref(false);
    const originalNote = ref(null);

    const rules = ref(
      {
        required: value => !!value || 'Champs Requis',
        name: value => {
          const pattern = /^([A-Za-z\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*)$/;
          return pattern.test(value) || 'Valeur Invalide';
        },
        date: value => {
          const pattern = /^([0]?[1-9]|[1|2][0-9]|[3][0|1])[/]([0]?[1-9]|[1][0-2])[/]([0-9]{4})$/;
          return pattern.test(value) || 'Date Invalide';
        },
        counter: value => value.length <= 20 || 'Max 20 characters',
        email: value => {
          const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          return pattern.test(value) || 'Email Invalide';
        }
      }
    );

    const crmProfileRedirectButtons = computed(() => {
      const parents = (props.customer?.contacts_from || []).filter(parent => parent.digital_account);
      const parentsButtons = [];

      if(parents.length) {
        parents.forEach(parent => {
          parentsButtons.push({ label: `Fiche Payeur (${parent.firstname} ${parent.lastname})`, path: `customer/${parent.uuid}` });
        });
      }

      return [
        { label: 'Fiche CRM', path: `customer/${props.customer.uuid}`},
        ...parentsButtons
      ]
    })

    const editableCustomer = ref({ ...props.customer });

    const genders = ref([
      { value: 'mr', label: 'Monsieur' },
      { value: 'mme', label: 'Madame' }
    ]);

    const genderLabel = (options, gender) => {
      const label = options.find(g => g.value === gender);
      return label ? label.label : 'Unkown';
    };

    const getCountryLabel = (options, code) => {
      const country = options.find(c => c.value === code);
      return country ? country.label : null;
    };

    const formVariables = ref({
      genders: genders,
      countries: countries,
      genderLabel: genderLabel,
      getCountryLabel: getCountryLabel
    });

    const saveCustomer = async () => {
      loading.value = true;
      const response = await updateCustomer(editableCustomer.value);

      editable.value = false;
      loading.value = false;

      if (response.success) {
        const text = 'Client mis à jour avec succès';
        sendNotification('success', text);
        editableCustomer.value.fullname = `${editableCustomer.value.firstname} ${editableCustomer.value.lastname.toUpperCase()}`;
        context.emit('updateContentType', editableCustomer.value);
        return;
      }

      const text = 'Erreur lors de la mise à jour du client';
      sendNotification('error', text);
    };

    const saveCustomerNotes = async () => {
      loadingNotes.value = true;
      const response = await updateCustomer(editableCustomer.value);

      editableNotes.value = false;
      loadingNotes.value = false;

      if (response.success) {
        const text = 'Client mis à jour avec succès';
        sendNotification('success', text);
        context.emit('updateContentType', editableCustomer.value);
        return;
      }

      const text = 'Erreur lors de la mise à jour du client';
      sendNotification('error', text);
    };

    const isRequired = field => field.rules && field.rules.some(f => f === 'required');

    const fieldItems = field => {
      if (field.hasOwnProperty('computedOptions')) {
        return formVariables.value[field.computedOptions];
      }
      return field.options;
    };

    const getFieldRules = field => {
      if (!field.rules) {
        return [];
      }

      return field.rules.map(rule => (rules.value[rule]));
    };

    const getLabel = field => {
      if (field.text) {
        return formVariables.value[field.text.name](formVariables.value[field.computedOptions], props.customer[field.field_name]);
      }

      if (field.viewType === 'Date Picker') {
        return formatDate(props.customer[field.field_name], field.format);
      }
      return props.customer[field.field_name];
    };

    onMounted(() => {
      if(editableCustomer.value && editableCustomer.value.notes){
        originalNote.value = editableCustomer.value.notes
      }
    })

    const changedNotes = computed(() => {
      return (editableCustomer.value.notes === originalNote.value) || (originalNote.value === null && editableCustomer.value.notes === "")
    })

    return { getLabel, getFieldRules, fieldItems, formVariables, isRequired, formFields, loading, countries, saveCustomer, editable, formValid, editableCustomer, mandatoryRule, genderLabel, genders, formatDate, getCountryLabel, editableNotes, loadingNotes, saveCustomerNotes, originalNote, changedNotes, crmProfileRedirectButtons, redirectToCRM };
  }
};
</script>

<style>

.default-cursor > div > div > div > textarea {
  cursor: default;
}
</style>
